import React, { useEffect, useState } from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import Button from "Components/common/Button";
import InputTextBox from "Components/common/InputTextBox";
import { toasterState } from "Components/common/toaster";
import userDetail from "Stores/userDetail";
import { accountsState } from "../AccountsState";
import "./addAccount.scss";
import "./editAccountPopup.scss";
import {
  parseCsvText,
  mapHeaders,
  rowToAccount,
  valuesToAccount
} from "./accountCsv";

// Same card-header-body-footer shell the Add Contact popup uses
// (Components/common/AddSingleContactPopup.js), scoped to accounts here
// via editAccountPopup.scss instead of the contacts-only stylesheet.
const SectionCard = ({ title, children, className = "" }) => (
  <section className={`editPopupCard ${className}`}>
    <header className="editPopupCardHeader">
      <h3 className="editPopupCardTitle">{title}</h3>
    </header>
    <div className="editPopupCardBody">{children}</div>
  </section>
);

// Up to 50 rows are created right away. Larger files (up to 5000 rows) are queued
// as a background bulk operation (Phase 4) and show progress in Bulk operations.
const MAX_ROWS_PER_UPLOAD = 50;
const MAX_ROWS_PER_JOB = 5000;
// Same cap as the contacts CSV mapping table (BulkContactTable.js's
// CSV_PREVIEW_ROW_LIMIT) for how many rows feed the "Data Preview" column.
const CSV_PREVIEW_ROW_LIMIT = 25;

const BASE_FIELDS = [
  { id: "domain", text: "Domain (required)", placeHolder: "salesgear.io" },
  { id: "name", text: "Company name" },
  { id: "industry", text: "Industry" },
  { id: "employeeCount", text: "Employee count" },
  { id: "revenue", text: "Revenue" },
  { id: "linkedinId", text: "LinkedIn URL" },
  { id: "secondaryWebsites", text: "Secondary domains (separate with ;)" },
  { id: "phone", text: "Phone" },
  { id: "otherPhone", text: "Other phone" }
];

const ACCOUNT_LABELS = {
  domain: "Domain",
  name: "Company name",
  industry: "Industry",
  employeeCount: "Employee count",
  revenue: "Revenue",
  linkedinId: "LinkedIn URL",
  secondaryWebsites: "Secondary domains",
  phone: "Phone",
  otherPhone: "Other phone"
};

class AddAccountPopupState {
  @observable showPopup = false;
  @observable onDone = () => {};
  @observable mode = "single";

  @action
  setShowPopup = (val = false) => {
    this.showPopup = val;
  };

  @action
  setMode = (val = "single") => {
    this.mode = val;
  };

  @action
  setOnDone = (val = () => {}) => {
    this.onDone = val;
  };
}

const addAccountPopupState = new AddAccountPopupState();

const summarise = body => ({
  created: body.created || 0,
  duplicates: body.duplicates || 0,
  failed: body.failed || 0,
  domainMissing: body.domainMissing || 0,
  genericDomains: body.genericDomains || 0
});

const AddAccountPopup = observer(() => {
  const mode = addAccountPopupState.mode;
  const [labels, setLabels] = useState({});
  const [singleValues, setSingleValues] = useState({});
  const [csv, setCsv] = useState({ headers: [], rows: [], mapping: [] });
  const [csvError, setCsvError] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [uploading, setUploading] = useState(false);

  // Same source and rule as the contact custom fields (ContactCustomFields.js) and the
  // account custom-fields settings page itself (settings/AccountCustomFields.js): the plan
  // decides how many slots are usable, not whether a label has been typed. A slot without a
  // saved label still shows, as "Custom Field N".
  const { customFieldsEnabled = false, customFieldsCount = 0 } =
    userDetail?.userFeatureFlag || {};

  const customInputs = Array.from({ length: customFieldsCount }, (value, index) => {
    const key = `customField${index + 1}`;
    return { id: key, text: labels[key] || `Custom Field ${index + 1}` };
  });

  const loadLabels = async () => {
    try {
      const r = await makeApi({ url: URLS.accountCustomFields, method: "GET" });
      if (r?.status === 200 && r.data) {
        setLabels(r.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const resetPopup = () => {
    setSingleValues({});
    setCsv({ headers: [], rows: [], mapping: [] });
    setCsvError("");
    setCsvFileName("");
    setUploading(false);
  };

  const closeAction = () => {
    addAccountPopupState.setShowPopup(false);
  };

  // Builds one toast line from the save counts, e.g.
  // "2 accounts added (1 already exists, 1 missing a domain skipped)".
  const summaryToastParts = (summary = {}) => {
    const parts = [];
    if (summary.duplicates) {
      parts.push(
        `${summary.duplicates} already ${summary.duplicates === 1 ? "exists" : "exist"}`
      );
    }
    if (summary.domainMissing) {
      parts.push(`${summary.domainMissing} missing a domain`);
    }
    if (summary.genericDomains) {
      parts.push(
        `${summary.genericDomains} generic domain${summary.genericDomains === 1 ? "" : "s"}`
      );
    }
    if (summary.failed) {
      parts.push(`${summary.failed} failed`);
    }
    return parts;
  };

  // A one-account submit (the single-account form) gets a plain sentence.
  // A CSV batch gets a count plus a breakdown of what was skipped and why.
  const toastSaveSummary = (summary = {}, isSingle = false) => {
    if (isSingle) {
      if (summary.created > 0) {
        toasterState.setToastMsg("Account added", "success");
      } else if (summary.duplicates) {
        toasterState.setToastMsg("This account already exists", "failure");
      } else if (summary.genericDomains) {
        toasterState.setToastMsg(
          "This domain is too generic to add as an account",
          "failure"
        );
      } else if (summary.domainMissing) {
        toasterState.setToastMsg("Domain is required", "failure");
      } else {
        toasterState.setToastMsg("Couldn't add the account", "failure");
      }
      return;
    }

    const skipped = summaryToastParts(summary);
    if (summary.created > 0) {
      let msg = `${summary.created} account${summary.created === 1 ? "" : "s"} added`;
      if (skipped.length) msg += ` (${skipped.join(", ")} skipped)`;
      toasterState.setToastMsg(msg, "success");
    } else {
      toasterState.setToastMsg(
        skipped.length ? `No accounts added: ${skipped.join(", ")}` : "No accounts added",
        "failure"
      );
    }
  };

  useEffect(() => {
    if (addAccountPopupState.showPopup) {
      resetPopup();
      loadLabels();
    }
  }, [addAccountPopupState.showPopup]);

  const changeSingleVal = (id = "", data = "") => {
    if (id) {
      setSingleValues({ ...singleValues, [id]: data || "" });
    }
  };

  const saveAccounts = async (accounts, localDomainMissing = 0) => {
    setUploading(true);
    try {
      const r = await makeApi({
        url: URLS.createAccounts,
        method: "POST",
        data: { accounts }
      });
      const body = r?.data || {};
      if (body.error) {
        toasterState.setToastMsg(body.error, "failure");
      } else {
        const summary = summarise(body);
        summary.domainMissing += localDomainMissing;
        toastSaveSummary(summary, accounts.length === 1);
        if (summary.created > 0) {
          if (addAccountPopupState.onDone) addAccountPopupState.onDone();
          closeAction();
        }
      }
    } catch (e) {
      console.error(e);
      toasterState.setToastMsg("Failed to create accounts", "failure");
    } finally {
      setUploading(false);
    }
  };

  const submitSingle = () => {
    const account = valuesToAccount(singleValues);
    if (!account.domain) {
      toasterState.setToastMsg("Domain is required", "failure");
      return;
    }
    saveAccounts([account]);
  };

  // Back to the dropzone, matching contacts' step flow (uploader, then table —
  // never both). Contacts reaches this by closing/reopening since its popup state
  // is reset on open; this gives the same result without the round trip.
  const changeCsvFile = () => {
    setCsv({ headers: [], rows: [], mapping: [] });
    setCsvError("");
    setCsvFileName("");
  };

  // Same pattern as the contacts CSV upload's sample download
  // (Components/common/bulkContactPopup/CsvUpload.js's downloadLink): a hardcoded
  // sample built from the real header aliases in accountCsv.js's ACCOUNT_CSV_FIELDS,
  // so it stays accurate to what mapHeaders() actually recognises.
  const downloadSampleCsv = () => {
    const header =
      "Domain,Account Name,Industry,Employee Count,Revenue,LinkedIn URL,Secondary Websites,Phone Number,Other Phone,Custom Field 1,Custom Field 2,Custom Field 3";
    const row =
      "salesgear.io,Salesgear,Computer Software,50,,https://www.linkedin.com/company/salesgear/,salesgear.com,650-410-8496,650-410-8496,Custom Text 1,Custom Text 2,Custom Text 3";
    const content = `${header}\n${row}`;
    const blob = new Blob(["﻿", content]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_salesgear_acc.csv");
    link.click();
    URL.revokeObjectURL(url);
  };

  const onFileChosen = e => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCsvText(ev.target.result);
      if (!parsed.headers.length) {
        setCsvError("The file is empty.");
        return;
      }
      const mapping = mapHeaders(parsed.headers, labels);
      if (!mapping.includes("domain")) {
        setCsvError("No domain column found. A domain column is required.");
        return;
      }
      if (parsed.rows.length > MAX_ROWS_PER_JOB) {
        setCsvError(
          `A file can have up to ${MAX_ROWS_PER_JOB} rows. Split the file and upload it in parts.`
        );
        return;
      }
      setCsvError("");
      setCsv({ headers: parsed.headers, rows: parsed.rows, mapping });
    };
    reader.readAsText(file);
  };

  const csvAccounts = csv.rows.map(cells => rowToAccount(cells, csv.mapping));
  const csvValid = csvAccounts.filter(account => account.domain);
  const csvMissingDomain = csvAccounts.length - csvValid.length;

  const queueAccountsJob = async (accounts, localDomainMissing = 0) => {
    setUploading(true);
    try {
      const r = await makeApi({
        url: URLS.createAccountsCsvJob,
        method: "POST",
        data: { accounts, fileName: csvFileName }
      });
      const body = r?.data || {};
      if (body.error) {
        toasterState.setToastMsg(body.error, "failure");
      } else {
        // Now tracked for real: AccountsTable polls accountBulkJobImportTracking and
        // shows the same progress bar (LinearProgressLoader/ImportProgressPopup) the
        // contacts CSV import uses. Fetch once immediately so the bar appears right
        // away instead of waiting for AccountsTable's own poll interval, which only
        // starts once it already sees an active job.
        accountsState.getBulkAccountJobImportTracking();
        toasterState.setToastMsg(
          `Upload queued. ${body.totalRows || accounts.length} accounts will be created in the background.`,
          "success"
        );
        closeAction();
      }
    } catch (e) {
      console.error(e);
      toasterState.setToastMsg("Failed to queue the upload", "failure");
    } finally {
      setUploading(false);
    }
  };

  const submitCsv = () => {
    if (!csvValid.length) {
      toasterState.setToastMsg("No rows with a domain to upload", "failure");
      return;
    }
    if (csvValid.length > MAX_ROWS_PER_UPLOAD) {
      queueAccountsJob(csvValid, csvMissingDomain);
      return;
    }
    saveAccounts(csvValid, csvMissingDomain);
  };

  const singleInputs = BASE_FIELDS.map(field => ({
    name: field.id,
    id: field.id,
    value: singleValues[field.id] || "",
    text: field.text,
    placeHolder: field.placeHolder || "",
    change: changeSingleVal
  }));

  const customFieldInputs = customInputs.map(field => ({
    name: field.id,
    id: field.id,
    value: singleValues[field.id] || "",
    text: field.text,
    change: changeSingleVal
  }));

  const mappedLabel = key => {
    if (!key) return "Not used";
    return ACCOUNT_LABELS[key] || labels[key] || key;
  };

  const buttonList = [
    {
      id: "addAccountCancelBtn",
      name: "addAccountCancelBtn",
      btnText: "Cancel",
      type: "button",
      className: "editPopupCancelBtn",
      click: closeAction
    },
    {
      id: "addAccountSubmitBtn",
      name: "addAccountSubmitBtn",
      btnText:
        mode === "single"
          ? "Create account"
          : `Upload ${csvValid.length} account${csvValid.length === 1 ? "" : "s"}`,
      type: "button",
      className: `editPopupSaveBtn ${uploading ? "loading" : ""}`,
      click: mode === "single" ? submitSingle : submitCsv,
      loader: uploading,
      disabled: mode === "csv" && (!csv.rows.length || uploading)
    }
  ];

  if (!addAccountPopupState.showPopup) {
    return null;
  }

  return (
    <div className="popupWrapper editAccountPopup editAccountPopupV2">
      <div className="editPopupMask" onClick={closeAction} />
      <div className="editPopupShell">
        <div className="editPopupForm">
          <header className="editPopupHeader">
            <div className="editPopupHeaderLeft">
              <h2 className="editPopupTitle">
                {mode === "csv" ? "Import accounts from CSV" : "Add account"}
              </h2>
              <p className="editPopupSubtitle">
                {mode === "csv"
                  ? "Upload a CSV file to add multiple accounts at once."
                  : "Fill in the account's details and click Create account to add it."}
              </p>
            </div>
            <button
              type="button"
              className="editPopupClose"
              onClick={closeAction}
              title="Close"
              aria-label="Close"
            >
              <i className="material-icons">close</i>
            </button>
          </header>

          <div className="editPopupBody">
            {mode === "single" && (
              <SectionCard title="Account Information">
                <div className="rowBreaker">
                  {singleInputs.map((item, index) => (
                    <InputTextBox {...item} key={`addAccount${index}`} />
                  ))}
                </div>
              </SectionCard>
            )}

            {mode === "single" && customFieldsEnabled && customFieldInputs.length > 0 && (
              <SectionCard title="Custom Fields" className="customFieldsSectionCard">
                <div className="rowBreaker">
                  {customFieldInputs.map((item, index) => (
                    <InputTextBox {...item} key={`addAccountCustom${index}`} />
                  ))}
                </div>
              </SectionCard>
            )}

            {mode === "csv" && (
              <SectionCard title="CSV File">
                <div className="addAccountCsv">
                  {csv.headers.length === 0 ? (
                    <>
                      {/* Same dropzone pattern as the contacts CSV upload
                          (Components/common/bulkContactPopup/CsvUpload.js): a dotted box
                          with a transparent file input laid over "Drag and drop or click
                          here". Contacts hides this as soon as a file is picked
                          (setShowCsvUploader(false) in AddBulkContactPopup.js) and shows
                          only the mapping table next — it never shows both together, so
                          this does the same instead of leaving the dropzone up underneath
                          the table. */}
                      <div className="addAccountUploadBox">
                        <div className="addAccountUploadInner">
                          {/* A plain character, not a material-icons-outlined ligature:
                              that glyph's advance width measured ~130px wider than its
                              painted icon, pushing the text far to the right of it.
                              Confirmed by swapping it for this and watching the gap
                              collapse to the intended 10px. */}
                          <span className="addAccountUploadIcon" aria-hidden="true">
                            ⬆
                          </span>
                          <div className="addAccountUploadCont">
                            <span>Drag and drop or click </span>
                            <span className="hereTxt">here</span>
                          </div>
                        </div>
                        <input
                          id="addAccountCsvFile"
                          className="addAccountFileInput"
                          type="file"
                          accept=".csv"
                          onChange={onFileChosen}
                        />
                      </div>
                      {csvError && <div className="addAccountError">{csvError}</div>}
                      <div className="addAccountSampleFile">
                        To download a sample CSV file, click
                        <span className="hereTxt" onClick={downloadSampleCsv}>
                          here
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="addAccountFileRow">
                        <span className="addAccountFileName">{csvFileName}</span>
                        <button
                          type="button"
                          className="addAccountChangeFile"
                          onClick={changeCsvFile}
                        >
                          Change file
                        </button>
                      </div>
                      <div className="addAccountCounts">
                        {csvMissingDomain > 0
                          ? `${csvAccounts.length} rows found, ${csvMissingDomain} skipped (no domain).`
                          : `${csvAccounts.length} account${
                              csvAccounts.length === 1 ? "" : "s"
                            } found.`}
                      </div>
                      {/* Same 3-column layout as the contacts CSV mapping step
                          (BulkContactTable.js: CSV Header / Salesgear Contact Field /
                          Data Preview), one row per CSV column. The mapping here is
                          read-only — there's no per-column override in the account CSV
                          path the way contacts' FieldMappingDropDown lets you remap a
                          column; this just shows what mapHeaders() already detected. */}
                      <div className="addAccountMappingTable">
                        <div className="addAccountMappingColumn">
                          <div className="addAccountColumnTitle">CSV Header</div>
                          {csv.headers.map((header, index) => (
                            <div className="addAccountColumnData" key={`csvHeader${index}`}>
                              <span className="addAccountColumnDataText">{header}</span>
                              {csv.mapping[index] === "domain" && (
                                <span className="required">*</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="addAccountMappingColumn">
                          <div className="addAccountColumnTitle">Account Field</div>
                          {csv.headers.map((header, index) => (
                            <div className="addAccountColumnData" key={`mappedField${index}`}>
                              <span className="addAccountColumnDataText">
                                {mappedLabel(csv.mapping[index])}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="addAccountMappingColumn addAccountDataColumn">
                          <div className="addAccountColumnTitle">Data Preview</div>
                          {csv.headers.map((header, index) => (
                            <div className="addAccountColumnData" key={`dataPreview${index}`}>
                              <span className="addAccountColumnDataText">
                                {csv.rows
                                  .slice(0, CSV_PREVIEW_ROW_LIMIT)
                                  .map(row => row[index])
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </SectionCard>
            )}
          </div>

          <footer className="editPopupFooter">
            {buttonList.map(item => (
              <Button key={item.id} {...item} />
            ))}
          </footer>
        </div>
      </div>
    </div>
  );
});

export { AddAccountPopup, addAccountPopupState };
export default AddAccountPopup;
